import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Link } from "@/i18n/LangRouting";
import { Play, Pause, RotateCcw, RotateCw, Globe, ChevronLeft, ChevronRight } from "lucide-react";
import { useExhibition } from "@/visitor/ExhibitionShell";
import { usePlayer } from "@/visitor/PlayerContext";
import Header from "@/visitor/Header";
import NudgeBanner from "@/visitor/NudgeBanner";
import ShopBlock from "@/visitor/ShopBlock";
import { fileUrl, fmtTime } from "@/lib/api";

function Controls({ a }) {
  const { track, playing, time, duration, rate, toggle, seek, skip, cycleRate, hasAudio } = usePlayer();
  const isCurrent = track?.artwork?.id === a.id;
  const enabled = isCurrent && hasAudio;
  return (
    <div data-testid="player-controls">
      <input type="range" className="seek" min={0} max={duration || 0} step={0.1} value={isCurrent ? Math.min(time, duration || 0) : 0} onChange={(e) => seek(Number(e.target.value))} disabled={!enabled} aria-label="Seek" data-testid="player-seek" />
      <div className="flex justify-between text-xs text-[var(--muted)] mt-1"><span data-testid="player-time">{fmtTime(isCurrent ? time : 0)}</span><span data-testid="player-duration">{fmtTime(isCurrent ? duration : 0)}</span></div>
      <div className="flex items-center justify-center gap-5 mt-4">
        <button type="button" className="ctrl relative" onClick={() => skip(-10)} disabled={!enabled} aria-label="Back 10 seconds" data-testid="player-back-10"><RotateCcw size={26} /><span className="absolute text-[0.55rem] font-bold">10</span></button>
        <button type="button" className="play-btn" onClick={toggle} disabled={!enabled} aria-label={playing ? "Pause" : "Play"} data-testid="player-toggle">
          {playing && isCurrent ? <Pause size={30} fill="currentColor" /> : <Play size={30} fill="currentColor" className="ml-1" />}
        </button>
        <button type="button" className="ctrl relative" onClick={() => skip(10)} disabled={!enabled} aria-label="Forward 10 seconds" data-testid="player-forward-10"><RotateCw size={26} /><span className="absolute text-[0.55rem] font-bold">10</span></button>
        <button type="button" className="ctrl text-sm font-bold" onClick={cycleRate} disabled={!enabled} aria-label="Playback speed" data-testid="player-speed">{rate}×</button>
      </div>
    </div>
  );
}

export default function ArtworkDetailPage() {
  const { slug, exhibition, lang, langName, openLanguageSheet, t, prefix } = useExhibition();
  const { artworkId } = useParams();
  const navigate = useNavigate();
  const { track, load } = usePlayer();
  const [tab, setTab] = useState("read");

  const idx = exhibition.artworks.findIndex((x) => x.slug === artworkId || x.id === artworkId);
  const a = exhibition.artworks[idx];
  const prev = exhibition.artworks[idx - 1];
  const next = exhibition.artworks[idx + 1];
  const c = a?.content?.[lang] || {};
  const textOnly = !c.audio_url;

  useEffect(() => {
    if (!a) return;
    if (track?.artwork?.id !== a.id || track?.lang !== lang) load(a, lang, slug);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [a?.id]);

  useEffect(() => { if (!a && exhibition.artworks.length) navigate(`${prefix}/e/${slug}`, { replace: true }); }, [a, exhibition, navigate, slug, prefix]);
  if (!a) return null;

  return (
    <div className="min-h-screen pb-10" data-testid="artwork-detail-page">
      <Header back={`/e/${slug}`} label={exhibition.name} />
      <NudgeBanner />
      <div className="lg:grid lg:grid-cols-2 lg:gap-12 max-w-6xl mx-auto px-5 lg:px-12 lg:pt-10">
        <div>
          <div className="aspect-[4/3] mt-5 lg:mt-0 overflow-hidden" data-testid="artwork-image">
            {a.image_url ? <img src={fileUrl(a.image_url)} alt={a.title} className="w-full h-full object-cover" /> : <div className="art-placeholder w-full h-full">{t("image_placeholder")}</div>}
          </div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl uppercase leading-[0.95] mt-6" data-testid="artwork-title">{a.title}</h1>
          <p className="text-[var(--muted)] mt-2" data-testid="artwork-meta">{a.place}{a.place && a.year ? " · " : ""}{a.year}</p>
        </div>
        <div className="mt-8 lg:mt-0">
          {textOnly ? (
            <div className="border border-dashed border-[var(--line)] py-6 text-center text-xs tracking-[0.3em] uppercase text-[var(--muted)]" data-testid="text-only-state">{t("text_only")} · {t("no_audio_in", { language: langName })}</div>
          ) : <Controls a={a} />}
          <button type="button" onClick={openLanguageSheet} data-testid="audio-language-button" className="mt-6 w-full flex items-center justify-between border border-[var(--line)] px-4 py-3 hover:border-white transition-colors">
            <span className="flex items-center gap-3 text-sm"><Globe size={18} className="text-accent" /> {t("audio_language")}</span>
            <span className="text-xs font-bold tracking-widest uppercase">{langName}</span>
          </button>
          {a.product && <ShopBlock product={a.product} exhibitionSlug={slug} artworkSlug={a.slug} t={t} discountActive={!!exhibition.gifts?.discount} discount={exhibition.gifts?.discount_percent || 25} />}
          <div className="flex border-b border-[var(--line)] mt-8" role="tablist">
            {[["read", t("read_along")], ["details", t("details")]].map(([k, label]) => (
              <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
                className={`px-4 py-3 text-sm tracking-wider uppercase border-b-2 -mb-px transition-colors ${tab === k ? "border-[var(--accent)] text-white" : "border-transparent text-[var(--muted)]"}`}>{label}</button>
            ))}
          </div>
          <div className="mt-5 text-[0.95rem] leading-relaxed text-[#d8d8d8] whitespace-pre-line min-h-[6rem]" data-testid="artwork-text">
            {tab === "read" ? (c.text || <span className="text-[var(--muted)]">{t("no_text_in", { language: langName })}</span>) : (
              <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 text-sm">
                <dt className="text-[var(--muted)]">{t("title")}</dt><dd>{a.title}</dd>
                <dt className="text-[var(--muted)]">{t("place")}</dt><dd>{a.place || "—"}</dd>
                <dt className="text-[var(--muted)]">{t("year")}</dt><dd>{a.year || "—"}</dd>
                <dt className="text-[var(--muted)]">{t("exhibition")}</dt><dd>{exhibition.name}</dd>
              </dl>
            )}
          </div>
          <nav className="flex justify-between items-center mt-10 border-t border-[var(--line)] pt-5">
            {prev ? <Link to={`/e/${slug}/a/${prev.slug}`} data-testid="artwork-prev" className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[var(--muted)] hover:text-white transition-colors"><ChevronLeft size={16} /> {t("previous")}</Link> : <span />}
            {next ? <Link to={`/e/${slug}/a/${next.slug}`} data-testid="artwork-next" className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-accent hover:brightness-110">{t("next")} <ChevronRight size={16} /></Link> : <span />}
          </nav>
        </div>
      </div>
    </div>
  );
}
